/**
 * Hook for managing chat functionality
 * Handles sending/receiving messages, conversation history, summaries, and exports
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../../../utils/api';
import toast from 'react-hot-toast';
import { ChatMessage, ConversationSummary, MessageEditRequest, MessageDeleteRequest } from '../types/chat.types';
import { MemoryInfo } from '../types';

export interface UseChatManagementReturn {
  chatMessages: ChatMessage[];
  isSending: boolean;
  isSimulating: boolean;
  activeConversationId: string | null;
  memoryInfo: MemoryInfo | null;
  error: string | null;
  conversationStats: { messageCount: number; totalTokens?: number } | null;
  sendMessage: (content: string, assistantId: string, config?: SendMessageConfig) => Promise<void>;
  loadConversationHistory: (sessionId: string) => Promise<void>;
  generateSummary: () => Promise<void>;
  clearChat: (assistantId?: string, sessionId?: string) => Promise<void>;
  clearConversation: (assistantId?: string, sessionId?: string) => Promise<void>;
  exportChat: (assistantName: string) => void;
  exportConversation: (assistantName: string) => void;
  editMessage: (request: MessageEditRequest) => Promise<void>;
  deleteMessage: (request: MessageDeleteRequest) => Promise<void>;
  addMessage: (message: ChatMessage) => void;
  setChatMessages: (messages: ChatMessage[]) => void;
  getMemoryInfo: (assistantId: string, conversationId: string) => Promise<void>;
  simulateConversation: (config: any) => Promise<void>;
  stopSimulation: () => void;
}

export interface SendMessageConfig {
  globalPrompt?: string;
  useGPT4?: boolean;
  sessionId?: string;
  agentId?: string;
  voiceEnabled?: boolean;
  onVoicePlayback?: (text: string) => void;
}

export const useChatManagement = (): UseChatManagementReturn => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [memoryInfo, setMemoryInfo] = useState<MemoryInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationStats, setConversationStats] = useState<{ messageCount: number; totalTokens?: number } | null>(null);

  /**
   * Send a message to the assistant
   */
  const sendMessage = useCallback(async (
    content: string,
    assistantId: string,
    config?: SendMessageConfig
  ) => {
    if (!content.trim() || isSending || !assistantId) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    // Add user message to chat
    setChatMessages(prev => [...prev, userMessage]);
    setIsSending(true);
    setError(null);

    // Create placeholder assistant message
    const messageId = `assist-${Date.now()}`;
    const placeholderMessage: ChatMessage = {
      id: messageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, placeholderMessage]);

    try {
      // Build request body, only including defined fields to avoid null validation errors
      const requestBody: any = {
        message: content.trim(),
        useGPT4: config?.useGPT4 ?? true
      };

      // Only include optional fields if they have values
      if (config?.globalPrompt) {
        requestBody.globalPrompt = config.globalPrompt;
      }
      if (config?.sessionId) {
        requestBody.sessionId = config.sessionId;
      }
      if (config?.agentId) {
        requestBody.agentId = config.agentId;
      }

      const chatResponse = await api.post(`/assistant-builder/chat/${assistantId}`, requestBody);

      // Update conversation ID
      if (chatResponse.data?.conversationId) {
        setActiveConversationId(chatResponse.data.conversationId);
      }

      // Update memory info
      if (chatResponse.data?.memory) {
        setMemoryInfo(chatResponse.data.memory);
      }

      // Update chat with assistant response
      if (chatResponse.data?.success && chatResponse.data?.response) {
        setChatMessages(prev => prev.map(m =>
          m.id === messageId
            ? {
                ...m,
                content: chatResponse.data.response,
                toolExecutions: chatResponse.data.toolExecutions
              }
            : m
        ));

        // Voice playback if enabled
        if (config?.voiceEnabled && config?.onVoicePlayback && chatResponse.data.response) {
          config.onVoicePlayback(chatResponse.data.response);
        }
      }

      setIsSending(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send message';
      setError(errorMessage);

      // Log full error for debugging
      console.error('💬 Chat error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });

      // Update placeholder with error message
      setChatMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, content: `Error: ${errorMessage}` }
          : m
      ));

      toast.error(errorMessage);
      console.error('Chat error:', err);
      setIsSending(false);
    }
  }, [isSending]);

  /**
   * Load conversation history by session ID
   */
  const loadConversationHistory = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      return;
    }

    setError(null);

    try {
      const response = await api.get(`/conversations/by-session/${sessionId}`);

      const messages = (response.data?.messages || []).map((m: any) => ({
        id: m.id,
        role: m.role === 'tester' ? 'user' : m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
        toolExecutions: m.toolExecutions,
        summary: m.summary
      })) as ChatMessage[];

      if (messages.length > 0) {
        setChatMessages(messages);
      }
    } catch (err: any) {
      // 404 is normal for new sessions
      if (err.response?.status === 404) {
        setChatMessages([]);
      } else {
        console.warn('Error fetching conversation history:', err);
      }
    }
  }, []);

  /**
   * Generate conversation summary
   */
  const generateSummary = useCallback(async () => {
    if (chatMessages.length < 2) {
      toast.error('Need at least one exchange to generate summary');
      return;
    }

    setError(null);

    try {
      // Filter out system messages
      const conversationMessages = chatMessages
        .filter(msg => msg.role !== 'system')
        .map(msg => ({
          content: msg.content,
          role: msg.role === 'tester' ? 'user' : msg.role,
          timestamp: msg.timestamp
        }));

      // Use server-side summary generation
      const summaryResponse = await api.post('/voice/generate-summary', {
        transcription: conversationMessages.map(m => `${m.role}: ${m.content}`).join('\n')
      });

      if (summaryResponse.data?.success && summaryResponse.data?.summary) {
        const summary = summaryResponse.data.summary;

        // Add summary to chat as a special message
        const summaryMessage: ChatMessage = {
          id: 'conversation-summary',
          role: 'system',
          content: '',
          timestamp: new Date(),
          summary: summary,
          isConversationSummary: true
        };

        setChatMessages(prev => [...prev, summaryMessage]);
        toast.success('📋 Conversation summary generated!');
      } else {
        throw new Error('Invalid summary response');
      }
    } catch (err: any) {
      // Generate fallback summary
      const conversationMessages = chatMessages.filter(msg => msg.role !== 'system');
      const userMessages = conversationMessages.filter(msg => msg.role === 'user' || msg.role === 'tester');
      const assistantMessages = conversationMessages.filter(msg => msg.role === 'assistant');

      const fallbackSummary: ConversationSummary = {
        id: 'fallback-summary',
        title: 'Conversation Summary',
        summary: `Conversation with ${userMessages.length} user messages and ${assistantMessages.length} assistant responses`,
        keyPoints: [
          `Customer initiated conversation with: "${userMessages[0]?.content || 'Initial inquiry'}"`,
          `Assistant provided ${assistantMessages.length} responses`,
          `Conversation covered ${conversationMessages.length} exchanges`
        ],
        sentiment: 'neutral',
        entities: [],
        createdAt: new Date()
      };

      const summaryMessage: ChatMessage = {
        id: 'conversation-summary-fallback',
        role: 'system',
        content: '',
        timestamp: new Date(),
        summary: fallbackSummary,
        isConversationSummary: true
      };

      setChatMessages(prev => [...prev, summaryMessage]);
      toast.success('📋 Summary generated (fallback mode)');
      console.warn('Failed to generate server summary, using fallback:', err);
    }
  }, [chatMessages]);

  /**
   * Clear chat history and reset session
   */
  const clearChat = useCallback(async (assistantId?: string, sessionId?: string) => {
    setError(null);

    // Clear memory if needed
    if (memoryInfo?.hasMemory && assistantId && sessionId) {
      try {
        await api.post(`/assistant-builder/chat/${assistantId}`, {
          message: '',
          clearMemory: true,
          sessionId: sessionId
        });
      } catch (err) {
        console.error('Failed to clear memory:', err);
      }
    }

    // Reset chat
    setMemoryInfo(null);
    setActiveConversationId(null);
    setChatMessages([]);
    toast.success('Chat cleared');
  }, [memoryInfo]);

  /**
   * Export chat to text file
   */
  const exportChat = useCallback((assistantName: string) => {
    if (chatMessages.length === 0) {
      toast.error('No messages to export');
      return;
    }

    const chatText = chatMessages
      .filter(msg => !msg.isConversationSummary) // Exclude summary messages
      .map(msg => {
        const sender = msg.role === 'user' ? 'User' : assistantName || 'Assistant';
        return `${sender}: ${msg.content}`;
      })
      .join('\n\n');

    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${assistantName || 'assistant'}-chat-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Chat exported successfully');
  }, [chatMessages]);

  /**
   * Edit a message
   */
  const editMessage = useCallback(async (request: MessageEditRequest) => {
    if (!request.conversationId || !request.messageId) {
      toast.error('Invalid edit request');
      return;
    }

    setError(null);

    try {
      await api.post(`/conversations/${request.conversationId}/messages/edit`, {
        messageId: request.messageId,
        newContent: request.newContent
      });

      // Update local state
      setChatMessages(prev => prev.map(m =>
        m.id === request.messageId
          ? { ...m, content: request.newContent }
          : m
      ));

      toast.success('Message updated');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to edit message';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to edit message:', err);
    }
  }, []);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback(async (request: MessageDeleteRequest) => {
    if (!request.conversationId || !request.messageId) {
      toast.error('Invalid delete request');
      return;
    }

    setError(null);

    try {
      await api.post(`/conversations/${request.conversationId}/messages/delete`, {
        messageId: request.messageId
      });

      // Update local state
      setChatMessages(prev => prev.filter(m => m.id !== request.messageId));
      toast.success('Message deleted');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to delete message';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to delete message:', err);
    }
  }, []);

  /**
   * Add a message to the chat
   */
  const addMessage = useCallback((message: ChatMessage) => {
    setChatMessages(prev => [...prev, message]);
  }, []);

  /**
   * Get memory info for a conversation
   */
  const getMemoryInfo = useCallback(async (assistantId: string, conversationId: string) => {
    try {
      const response = await api.get(`/assistant-builder/memory/${assistantId}/${conversationId}`);
      if (response.data) {
        setMemoryInfo(response.data);
      }
    } catch (err) {
      console.warn('Failed to fetch memory info:', err);
    }
  }, []);

  /**
   * Simulate a conversation
   */
  const simulateConversation = useCallback(async (config: any) => {
    setIsSimulating(true);
    try {
      // Simulation logic would go here
      toast.success('Conversation simulation started');
    } catch (err) {
      toast.error('Failed to start simulation');
      console.error('Simulation error:', err);
    }
  }, []);

  /**
   * Stop ongoing simulation
   */
  const stopSimulation = useCallback(() => {
    setIsSimulating(false);
    toast.success('Simulation stopped');
  }, []);

  // Update conversation stats whenever messages change
  useEffect(() => {
    if (chatMessages.length > 0) {
      setConversationStats({
        messageCount: chatMessages.length,
        totalTokens: undefined // Could calculate if token info is available
      });
    }
  }, [chatMessages]);

  return {
    chatMessages,
    isSending,
    isSimulating,
    activeConversationId,
    memoryInfo,
    error,
    conversationStats,
    sendMessage,
    loadConversationHistory,
    generateSummary,
    clearChat,
    clearConversation: clearChat, // Alias
    exportChat,
    exportConversation: exportChat, // Alias
    editMessage,
    deleteMessage,
    addMessage,
    setChatMessages,
    getMemoryInfo,
    simulateConversation,
    stopSimulation
  };
};
